/**
 * UnifiedAnalytics - Single comprehensive analytics dashboard with all analytics sections
 * Combines: Site Statistics, Analytics, Advanced Analytics, Campaign Analytics, Live Analytics
 */
import { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Target, Activity, Users, DollarSign,
  Eye, ShoppingCart, Package, Calendar, FlaskConical, Bell, Coffee, Award
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Import all existing analytics components
import { 
  AnalyticsDashboard,
  EnhancedAnalytics,
  AdvancedAnalyticsDashboard,
  CampaignAnalyticsDashboard,
  RealTimeAnalytics,
  SiteStatisticsTab,
  ABTestingDashboard,
  PushNotificationDashboard
} from '@/components/admin/analytics';

// Import new comprehensive reports
import SalesReportsTab from './SalesReportsTab';
import CoffeePassAnalyticsTab from './CoffeePassAnalyticsTab';

export default function UnifiedAnalytics() {
  const [activeSubTab, setActiveSubTab] = useState('sales');
  const [adminToken, setAdminToken] = useState(null);

  // Get admin token from localStorage
  useEffect(() => {
    const adminData = localStorage.getItem('bulaccino-admin');
    if (adminData) {
      try {
        const parsed = JSON.parse(adminData);
        setAdminToken(parsed.access_token || parsed.token || '');
      } catch (e) {
        console.error('Failed to parse admin token:', e);
      }
    }
  }, []);

  return (
    <div className="space-y-6" data-testid="unified-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Analytics Hub</h2>
          <p className="text-muted-foreground">Complete analytics for all aspects of your business</p>
        </div>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1 mb-6 bg-muted/50 rounded-xl">
          <TabsTrigger 
            value="sales" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-green-100 data-[state=active]:text-green-800 dark:data-[state=active]:bg-green-900/40 dark:data-[state=active]:text-green-200 data-[state=active]:shadow"
            data-testid="analytics-sales-tab"
          >
            <Award className="w-4 h-4" />
            <span>Sales Reports</span>
          </TabsTrigger>
          <TabsTrigger 
            value="coffeepass" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-800 dark:data-[state=active]:bg-amber-900/40 dark:data-[state=active]:text-amber-200 data-[state=active]:shadow"
            data-testid="analytics-coffeepass-tab"
          >
            <Coffee className="w-4 h-4" />
            <span>Bula Pass</span>
          </TabsTrigger>
          <TabsTrigger 
            value="overview" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-card data-[state=active]:shadow"
            data-testid="analytics-overview-tab"
          >
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger 
            value="live" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-card data-[state=active]:shadow"
            data-testid="analytics-live-tab"
          >
            <Activity className="w-4 h-4 text-green-500" />
            <span className="hidden sm:inline">Live</span>
          </TabsTrigger>
          <TabsTrigger 
            value="campaigns" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-card data-[state=active]:shadow"
            data-testid="analytics-campaigns-tab"
          >
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Campaigns</span>
          </TabsTrigger>
          <TabsTrigger 
            value="customers" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-card data-[state=active]:shadow"
            data-testid="analytics-customers-tab"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Customers</span>
          </TabsTrigger>
          <TabsTrigger 
            value="products" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-card data-[state=active]:shadow"
            data-testid="analytics-products-tab"
          >
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Products</span>
          </TabsTrigger>
          <TabsTrigger 
            value="site" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-card data-[state=active]:shadow"
            data-testid="analytics-site-tab"
          >
            <Eye className="w-4 h-4" />
            <span className="hidden sm:inline">Site Stats</span>
          </TabsTrigger>
          <TabsTrigger 
            value="abtesting" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-card data-[state=active]:shadow"
            data-testid="analytics-abtesting-tab"
          >
            <FlaskConical className="w-4 h-4" />
            <span className="hidden sm:inline">A/B Tests</span>
          </TabsTrigger>
          <TabsTrigger 
            value="push" 
            className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-card data-[state=active]:shadow"
            data-testid="analytics-push-tab"
          >
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Push</span>
          </TabsTrigger>
        </TabsList>

        {/* NEW: Sales Reports - Top sellers, item sales, summary */}
        <TabsContent value="sales" className="mt-0">
          <SalesReportsTab />
        </TabsContent>

        {/* NEW: Coffee Pass Analytics - Redemption insights */}
        <TabsContent value="coffeepass" className="mt-0">
          <CoffeePassAnalyticsTab />
        </TabsContent>

        {/* Overview - Main Analytics Dashboard */}
        <TabsContent value="overview" className="mt-0">
          <AnalyticsDashboard />
        </TabsContent>

        {/* Live Analytics - Real-time data */}
        <TabsContent value="live" className="mt-0">
          <RealTimeAnalytics />
        </TabsContent>

        {/* Campaign Analytics */}
        <TabsContent value="campaigns" className="mt-0">
          <CampaignAnalyticsDashboard />
        </TabsContent>

        {/* Customer Segmentation & Advanced Analytics */}
        <TabsContent value="customers" className="mt-0">
          <AdvancedAnalyticsDashboard />
        </TabsContent>

        {/* Product Performance */}
        <TabsContent value="products" className="mt-0">
          <EnhancedAnalytics />
        </TabsContent>

        {/* Site Statistics */}
        <TabsContent value="site" className="mt-0">
          <SiteStatisticsTab token={adminToken} />
        </TabsContent>

        {/* A/B Testing Dashboard */}
        <TabsContent value="abtesting" className="mt-0">
          <ABTestingDashboard />
        </TabsContent>

        {/* Push Notification Dashboard */}
        <TabsContent value="push" className="mt-0">
          <PushNotificationDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
